"use client";

import { useState, useEffect } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { motion } from "framer-motion";

type Industry = {
  _id: string;
  title: string;
  description?: string;
  subcategories?: Array<{
    _id: string;
    title: string;
    description?: string;
  }>;
};

type Service = {
  _id: string;
  name: string;
  description?: string;
  features?: string[];
  category?: {
    title: string;
  };
};

type CustomCard = {
  title: string;
  description?: string;
  tags?: string[];
  link?: {
    url?: string;
    text?: string;
  };
};

type Props = {
  tagline?: string;
  title?: string;
  description?: string;
  contentType?: "industries" | "services" | "custom";
  industries?: Industry[];
  services?: Service[];
  customCards?: CustomCard[];
  maxItems?: number;
  showTags?: boolean;
  gridLayout?: "2" | "3" | "4";
  styling?: {
    backgroundColor?: string;
    padding?: "sm" | "md" | "lg" | "xl";
    maxWidth?: "sm" | "md" | "lg" | "xl" | "container" | "full";
    cardSpacing?: "tight" | "normal" | "loose";
  };
};

const CardGridItem = ({
  item,
  index,
  contentType,
  showTags = true,
}: {
  item: Industry | Service | CustomCard;
  index: number;
  contentType: "industries" | "services" | "custom";
  showTags?: boolean;
}) => {
  
  // Extract data based on content type
  let title, description, tags, linkUrl, linkText;

  if (contentType === "industries") {
    const industry = item as Industry;
    title = industry.title;
    description = industry.description;
    tags = industry.subcategories?.map(sub => sub.title) || [];
    linkUrl = `/industries/${industry._id}`;
    linkText = "View Solutions";
  } else if (contentType === "services") {
    const service = item as Service;
    title = service.name;
    description = service.description;
    tags = service.features || [];
    linkUrl = `/services/${service._id}`;
    linkText = "Learn More";
  } else {
    const customCard = item as CustomCard;
    title = customCard.title;
    description = customCard.description;
    tags = customCard.tags || [];
    linkUrl = customCard.link?.url;
    linkText = customCard.link?.text || "View Details";
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.05 }}
      className="h-full group relative"
    >
      {/* Gradient background div */}
      <div 
        className="absolute rounded-xl transition-opacity duration-300 opacity-0 group-hover:opacity-75"
        style={{
          inset: '-20px',
          background: 
            'radial-gradient(at 15% 20%, hsla(24, 95%, 85%, 0.8) 0px, transparent 50%), radial-gradient(at 50% 20%, hsla(205, 95%, 85%, 0.7) 0px, transparent 50%), radial-gradient(at 85% 20%, hsla(160, 85%, 80%, 0.75) 0px, transparent 50%), radial-gradient(at 50% 70%, hsla(260, 95%, 88%, 0.7) 0px, transparent 50%)',
          filter: 'blur(20px)',
          zIndex: -1,
        } as React.CSSProperties}
      />
      
      <Card 
        className="h-full flex flex-col p-8 transition-all duration-300 border-border rounded-xl hover:scale-[1.02] relative bg-bg"
      >
        <CardHeader className="p-0 relative z-10">
          <p className="text-xs uppercase tracking-wider text-text-muted">
            {contentType === "industries" ? title : contentType === "services" ? (item as Service).category?.title || "SERVICE" : "CUSTOM"}
          </p>
          <CardTitle className="text-2xl text-heading font-normal mt-2 group-hover:text-brand-brown transition-colors duration-300">
            {contentType === "industries" ? `${title}` : title}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0 pt-4 flex-grow relative z-10">
          <p className="text-base text-text">{description}</p>
        </CardContent>
        {showTags && tags && tags.length > 0 && (
          <CardFooter className="p-0 pt-6 relative z-10">
            <div className="flex flex-wrap gap-2">
              {tags.map((tag, tagIndex) => (
                <Badge key={tagIndex} variant="secondary" className="font-medium group-hover:bg-brand-beige group-hover:text-brand-espresso transition-colors duration-300">
                  {tag}
                </Badge>
              ))}
            </div>
          </CardFooter>
        )}
      </Card>
    </motion.div>
  );
};

export function CardGrid(props: Props) {
  const {
    tagline,
    title,
    description,
    contentType = "industries",
    industries: manualIndustries,
    services: manualServices,
    customCards,
    maxItems,
    showTags = true,
    gridLayout = "3",
    styling = {}
  } = props;

  const [items, setItems] = useState<(Industry | Service | CustomCard)[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        let fetchedItems: (Industry | Service | CustomCard)[] = [];

        const fetchPublicItems = async (
          type: "allIndustries" | "allServices"
        ) => {
          const response = await fetch(`/api/public-content?type=${type}`, {
            method: "GET",
            cache: "force-cache",
          });
          if (!response.ok) {
            throw new Error(`Failed to fetch ${type}`);
          }
          return (await response.json()) as (Industry | Service)[];
        };

        if (contentType === "industries") {
          if (manualIndustries && manualIndustries.length > 0) {
            fetchedItems = manualIndustries;
          } else {
            fetchedItems = await fetchPublicItems("allIndustries");
          }
        } else if (contentType === "services") {
          if (manualServices && manualServices.length > 0) {
            fetchedItems = manualServices;
          } else {
            fetchedItems = await fetchPublicItems("allServices");
          }
        } else if (contentType === "custom") {
          fetchedItems = customCards || [];
        }

        if (maxItems && maxItems > 0) {
          fetchedItems = fetchedItems.slice(0, maxItems);
        }
        
        // If no items found and it's industries, show sample data
        if (fetchedItems.length === 0 && contentType === "industries") {
          fetchedItems = [
            {
              _id: "sample-1",
              title: "Healthcare",
              description: "Enhancing patient care and operational efficiency.",
              subcategories: [
                { _id: "sub-1", title: "Health IT", description: "Healthcare technology solutions" },
                { _id: "sub-2", title: "Telemedicine", description: "Remote healthcare services" }
              ]
            },
            {
              _id: "sample-2", 
              title: "Retail",
              description: "Powering seamless online and offline retail experiences.",
              subcategories: [
                { _id: "sub-3", title: "E-commerce", description: "Online retail platforms" },
                { _id: "sub-4", title: "POS Systems", description: "Point of sale solutions" }
              ]
            },
            {
              _id: "sample-3",
              title: "Real Estate", 
              description: "Transforming property management with digital tools.",
              subcategories: [
                { _id: "sub-5", title: "PropTech", description: "Property technology solutions" },
                { _id: "sub-6", title: "Construction", description: "Construction management tools" }
              ]
            }
          ];
        }
        
        setItems(fetchedItems);
      } catch (error) {
        setItems([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [contentType, manualIndustries, manualServices, customCards, maxItems]);

  // Dynamic classes
  const bgClass = styling?.backgroundColor || "";
  const paddingClass = {
    sm: "py-8",
    md: "py-12", 
    lg: "py-16",
    xl: "py-20"
  }[styling?.padding || "lg"];
  
  const maxWidthClass = {
    container: "",
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg", 
    xl: "max-w-6xl",
    full: "max-w-full"
  }[styling?.maxWidth || "container"];

  const spacingClass = {
    tight: "gap-4",
    normal: "gap-8",
    loose: "gap-12"
  }[styling?.cardSpacing || "normal"];

  const gridColsClass = {
    "2": "grid-cols-1 sm:grid-cols-2",
    "3": "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    "4": "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4"
  }[gridLayout];

  if (loading) {
    return (
      <section className={`${bgClass} ${paddingClass}`}>
        <div className={`container mx-auto px-6 ${maxWidthClass}`}>
          <div className="text-center">
            <p className="text-text">Loading...</p>
          </div>
        </div>
      </section>
    );
  }

  if (items.length === 0) {
    return null;
  }

  return (
    <section className={`${bgClass} ${paddingClass}`}>
      <div className={`container mx-auto px-6 ${maxWidthClass}`}>
        {(tagline || title || description) && (
          <header className="mb-4 max-w-lg md:mb-18 lg:mb-20">
            {tagline && <p className="mb-3 text-subheading uppercase md:mb-4">{tagline}</p>}
            {title && (
              <h2 className="mb-5 text-3xl text-heading font-normal">
                {title}
              </h2>
            )}
            {description && <p className="md:text-md text-text">{description}</p>}
          </header>
        )}
        
        <div className={`grid ${gridColsClass} ${spacingClass}`}>
          {items.map((item, index) => (
            <CardGridItem
              key={('_id' in item ? item._id : null) || index}
              item={item}
              index={index}
              contentType={contentType}
              showTags={showTags}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
